import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  Text,
} from '@chakra-ui/react';
import {
  Controller,
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormSetError,
  UseFormClearErrors,
} from 'react-hook-form';
import VideoIcon from '@/assets/svg/video-icon.svg?react';
import DeleteIcon from '@/assets/svg/delete-bg.svg?react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { useState } from 'react';
import Asterisk from '../common/Asterisk';
import { type FormData } from '@/services/addProduct/types';
import { IProductEditResponse } from '@/services/manageProduct/types';

interface VideoUploadSectionProps {
  control: Control<FormData>;
  errors: FieldErrors<FormData>;
  product?: IProductEditResponse;
  setValue: UseFormSetValue<FormData>;
  clearErrors: UseFormClearErrors<FormData>;
  setError: UseFormSetError<FormData>;
}

const VideoUploadSection = ({
  control,
  errors,
  product,
  setValue,
  clearErrors,
  setError,
}: VideoUploadSectionProps) => {
  const [uploadedVideo, setUploadedVideo] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [videoRemoved, setVideoRemoved] = useState(false);

  const handleVideoUpload = (file: File) => {
    setUploadedVideo(file);
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 500);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];

    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('productDetails.video', {
          type: 'manual',
          message: 'File size must be less than 10MB',
        });
        return;
      }

      clearErrors('productDetails.video');
      setValue('productDetails.video', file);
      handleVideoUpload(file);
    }
  };

  return (
    <FormControl isInvalid={!!errors.productDetails?.video}>
      <FormLabel fontSize="sm" color="text.secondary">
        Upload Video (Optional)
        <Asterisk color="#0000" />
      </FormLabel>

      {product?.video && !videoRemoved ? (
        <Box
          p={4}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="sm"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          bg="gray.50"
        >
          <video width="150" height="50" controls>
            <source src={product.video} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <Button
            size="sm"
            colorScheme="brand.primary"
            onClick={() => {
              setVideoRemoved(true);
              setUploadedVideo(null);
              setValue('productDetails.video', undefined);
            }}
          >
            Edit
          </Button>
        </Box>
      ) : (
        <Box
          as="label"
          htmlFor="upload-video"
          display={uploadedVideo ? 'none' : 'flex'}
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          border="2px dashed"
          borderColor="gray.400"
          borderRadius="md"
          p={4}
          cursor="pointer"
          transition="all 0.2s"
          _hover={{ bg: 'gray.100' }}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <Icon as={VideoIcon} w={8} h={8} color="gray.500" />
          <Text mt={2} color="gray.500">
            Upload or drag and drop
          </Text>
          <Text fontSize="sm" color="gray.400">
            mp4, webm, mov, avi format is acceptable - max 10mb
          </Text>
          <Controller
            name="productDetails.video"
            control={control}
            render={({ field }) => (
              <Input
                id="upload-video"
                type="file"
                accept="video/*"
                hidden
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const file = e.target.files?.[0] || null;

                  if (file) {
                    if (file.size > 10 * 1024 * 1024) {
                      setError('productDetails.video', {
                        type: 'manual',
                        message: 'File size must be less than 10MB',
                      });
                      return;
                    }

                    clearErrors('productDetails.video');
                    field.onChange(file);
                    handleVideoUpload(file);
                    e.target.value = '';
                  }
                }}
              />
            )}
          />
        </Box>
      )}

      {uploadedVideo && (
        <Box
          p={4}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="sm"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          bg="gray.50"
        >
          <Box display="flex" alignItems="center" gap={4}>
            <Icon as={VideoIcon} w={6} h={6} color="blue.500" />
            <Box>
              <Text fontSize="sm" fontWeight="medium">
                {uploadedVideo.name}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {(uploadedVideo.size / (1024 * 1024)).toFixed(2)} MB
              </Text>
              {uploadProgress < 100 && (
                <Box mt={2} h="4px" w="full" bg="gray.200" borderRadius="md">
                  <Box
                    h="4px"
                    bg="red.500"
                    borderRadius="md"
                    width={`${uploadProgress}%`}
                    transition="width 0.2s"
                  />
                </Box>
              )}
              {uploadProgress === 100 && (
                <Flex align={'center'} gap={1}>
                  <Text fontSize="xs" mt={1} color="green.500">
                    Upload Complete!
                  </Text>
                  <CheckCircleIcon color={'green'} boxSize={3} />
                </Flex>
              )}
            </Box>
          </Box>
          <Icon
            as={DeleteIcon}
            w={5}
            h={5}
            color="red.500"
            cursor="pointer"
            onClick={() => {
              setUploadedVideo(null);
              setUploadProgress(0);
              setValue('productDetails.video', undefined);
            }}
          />
        </Box>
      )}
      <FormErrorMessage>{errors.productDetails?.video?.message}</FormErrorMessage>
    </FormControl>
  );
};

export default VideoUploadSection;
